import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import type { User, IWorkFlow, IAppConfig } from '../../features/auth/auth.types';

// Re-export types so existing imports from this file keep working
export type { User, IWorkFlow, IAppConfig };

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  showVerificationPending: boolean;
  workflows: IWorkFlow[] | null;
  appConfig: IAppConfig | null;
  /** Tracks which product is selected in the sidebar switcher (pension / salary).
   *  UI-only state — not server data. */
  selectedProductObj: Record<string, string>;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  showVerificationPending: false,
  workflows: [],
  appConfig: null,
  selectedProductObj: {},
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login: (state, action: PayloadAction<User>) => {
      state.isAuthenticated = true;
      state.user = action.payload;
      localStorage.setItem('PenUser', JSON.stringify(action.payload));
    },
    setShowVerificationPending: (state, action: PayloadAction<boolean>) => {
      state.showVerificationPending = action.payload;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.showVerificationPending = false;
      localStorage.removeItem('PenUser');
    },
    update: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      localStorage.setItem('user', JSON.stringify(action.payload));
    },
    updateUserType: (state, action: PayloadAction<string>) => {
      if (state.user) {
        state.user.usertype = action.payload;
      }
    },
    setWorkflows: (state, action: PayloadAction<IWorkFlow[] | null>) => {
      state.workflows = action.payload;
    },
    setAppConfig: (state, action: PayloadAction<IAppConfig | null>) => {
      state.appConfig = action.payload;
    },
    setActiveEmployerCode: (state, action: PayloadAction<string>) => {
      if (state.user) {
        state.user.employerCode = action.payload;
      }
    },
    /** Updates the selected product in the sidebar switcher dropdown */
    setProduct: (state, action: PayloadAction<Record<string, string>>) => {
      state.selectedProductObj = action.payload;
    },
  },
});

export const {
  login,
  logout,
  update,
  updateUserType,
  setShowVerificationPending,
  setWorkflows,
  setAppConfig,
  setActiveEmployerCode,
  setProduct,
} = authSlice.actions;

// ── Selectors ─────────────────────────────────────────────────────────────────

export const selectUser = (state: RootState) => state.auth.user;

export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;

export const selectActiveEmployerCode = (state: RootState) =>
  state.auth.user?.employerCode || state.auth.user?.defaultEmployerCode || '';

export const selectWorkflows = (state: RootState) => state.auth.workflows;

export const selectAppConfig = (state: RootState) => state.auth.appConfig;

export default authSlice.reducer;
